// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Trigger regions — rollovers, lane switches, mode detectors (M1-14).
//
// A trigger has no geometry a ball can touch. It watches a region and reports entry and
// exit. Two details separate a trigger that works from one that produces phantom scores:
//
//   * a **height range**, so a ball flying over a rollover on an airball does not roll over
//     it, and a ball on a ramp above a playfield switch does not close it; and
//   * a **direction filter**, because a lane that only counts when entered from above is a
//     different lane from one that counts either way, and orbit shots routinely re-enter a
//     lane backwards on the way down.
//
// Debounce is in steps, never milliseconds, so it is identical on every runtime (PH-086).

import type { Ball } from '../Ball'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_SWITCH_CLOSED, EV_SWITCH_OPENED } from '../events'
import { MAX_BALLS } from '../constants'

export type TriggerRegion =
  | { readonly shape: 'circle'; readonly cx: number; readonly cy: number; readonly radius: number }
  | {
      readonly shape: 'rect'
      readonly minX: number
      readonly minY: number
      readonly maxX: number
      readonly maxY: number
    }

export interface TriggerConfig {
  readonly id: string
  readonly region: TriggerRegion
  /**
   * Required entry direction, a unit vector. The ball's velocity must point within
   * `acceptCosine` of it. Omit to accept entry from any direction.
   */
  readonly direction?: readonly [number, number]
  /** Cosine of the widest accepted deviation from `direction`. Default 0 — a half-plane. */
  readonly acceptCosine?: number
  /** Steps before the same trigger can close again. Default 60. */
  readonly debounceSteps?: number
  /** Height band the ball centre must lie in, metres above the playfield. */
  readonly zRange?: readonly [number, number]
}

export class Trigger extends Device {
  readonly kind = 'trigger'
  override readonly wantsObserve = true

  /** Public so a renderer or the Builder can draw the zone without duplicating it. */
  readonly region: TriggerRegion
  private readonly dirX: number
  private readonly dirY: number
  private readonly hasDirection: boolean
  private readonly acceptCosine: number
  private readonly debounceSteps: number
  private readonly zMin: number
  private readonly zMax: number

  private readonly inside = new Uint8Array(MAX_BALLS)
  private readonly closed = new Uint8Array(MAX_BALLS)
  private readonly lastCloseStep = new Int32Array(MAX_BALLS)

  protected override ownState(): DeviceState {
    // All three are per-ball and all three are cross-step. `inside` is "this ball is already in
    // the region", so a restore without it fires the switch again the moment the ball is looked
    // at; `lastCloseStep` is the debounce, so a restore without it fires again immediately after.
    // Both were missing until a chunked validation of one real replay scored 146,000 where the
    // uninterrupted run scored 126,000 — twenty thousand points of switches closing twice.
    return {
      inside: [...this.inside],
      closed: [...this.closed],
      lastCloseStep: [...this.lastCloseStep],
    }
  }

  protected override restoreOwn(state: DeviceState): void {
    const inside = state['inside']
    const closed = state['closed']
    const lastCloseStep = state['lastCloseStep']
    if (Array.isArray(inside)) this.inside.set(inside as number[])
    if (Array.isArray(closed)) this.closed.set(closed as number[])
    if (Array.isArray(lastCloseStep)) this.lastCloseStep.set(lastCloseStep as number[])
  }

  /** Balls currently holding this switch closed. The rule engine reads it as switch state. */
  get isClosed(): boolean {
    for (let i = 0; i < MAX_BALLS; i++) if (this.closed[i]! !== 0) return true
    return false
  }

  /** Last accepted closure, including one already released between render frames; no new state. */
  get closedAtStep(): number {
    let latest = -1
    for (let i = 0; i < MAX_BALLS; i++) latest = Math.max(latest, this.lastCloseStep[i]!)
    return latest
  }

  constructor(config: TriggerConfig) {
    super(config.id)
    this.region = config.region
    if (config.region.shape === 'circle') {
      this.setOrigin(config.region.cx, config.region.cy)
    } else {
      this.setOrigin(
        (config.region.minX + config.region.maxX) / 2,
        (config.region.minY + config.region.maxY) / 2,
      )
    }

    const d = config.direction
    this.hasDirection = d !== undefined
    if (d) {
      const len = Math.sqrt(d[0] * d[0] + d[1] * d[1])
      if (len === 0) throw new Error(`Trigger ${config.id}: zero-length direction`)
      this.dirX = d[0] / len
      this.dirY = d[1] / len
    } else {
      this.dirX = 0
      this.dirY = 0
    }

    this.acceptCosine = config.acceptCosine ?? 0
    this.debounceSteps = config.debounceSteps ?? 60
    this.zMin = config.zRange ? config.zRange[0] : -Infinity
    this.zMax = config.zRange ? config.zRange[1] : Infinity
    this.lastCloseStep.fill(-1_000_000)
  }

  private contains(ball: Ball): boolean {
    if (ball.z < this.zMin || ball.z > this.zMax) return false
    const r = this.region
    if (r.shape === 'circle') {
      const dx = ball.x - r.cx
      const dy = ball.y - r.cy
      return dx * dx + dy * dy <= r.radius * r.radius
    }
    return ball.x >= r.minX && ball.x <= r.maxX && ball.y >= r.minY && ball.y <= r.maxY
  }

  override observe(world: World, ball: Ball): void {
    const i = ball.id
    const inside = this.contains(ball)

    if (inside) {
      if (this.inside[i]! !== 0) return
      this.inside[i] = 1

      if (this.hasDirection) {
        const speed = ball.speed()
        if (speed < 1e-9) return
        const dot = (ball.vx * this.dirX + ball.vy * this.dirY) / speed
        if (dot < this.acceptCosine) return
      }
      if (world.stepIndex - this.lastCloseStep[i]! < this.debounceSteps) return

      this.lastCloseStep[i] = world.stepIndex
      this.closed[i] = 1
      world.events.emit(EV_SWITCH_CLOSED, this.index, i, world.stepIndex, ball.speed())
      return
    }

    if (this.inside[i]! === 0) return
    this.inside[i] = 0
    // Only report an opening for a closure that was actually reported. A ball filtered out
    // by direction or debounce never closed the switch, so it cannot open it either.
    if (this.closed[i]! !== 0) {
      this.closed[i] = 0
      world.events.emit(EV_SWITCH_OPENED, this.index, i, world.stepIndex, 0)
    }
  }

  override reset(): void {
    this.inside.fill(0)
    this.closed.fill(0)
    this.lastCloseStep.fill(-1_000_000)
  }
}
